<template>
  <div v-if="superuserDashboardDataLPExist === true">

    <!-- ===================== TOTAL SALES CHART ===================== -->
    <b-card class="mb-2">
      <div class="d-flex align-items-center justify-content-between flex-wrap mb-1">
        <h4 class="card-title mb-0">
          Total Sales {{ selectedSalesActivityYear }}
        </h4>
        <!-- Metric toggle -->
        <div class="d-flex flex-wrap gap-50">
          <b-button
            v-for="m in chartMetricOptions"
            :key="m.value"
            size="sm"
            :variant="selectedChartMetric === m.value ? 'primary' : 'outline-secondary'"
            class="mr-25 mb-25"
            @click="selectedChartMetric = m.value"
          >
            {{ m.label }}
          </b-button>
        </div>
      </div>
      <b-row class="mb-1 align-items-end">
        <b-col
          cols="6"
          sm="3"
          md="2"
          class="mb-1 mb-sm-0"
        >
          <small class="text-muted d-block mb-25">Year</small>
          <v-select
            v-model="selectedSalesActivityYear"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="yearOptions"
            :clearable="false"
            @input="fetchDealPerYearChart"
          />
        </b-col>
        <b-col
          cols="6"
          sm="3"
          md="3"
          class="mb-1 mb-sm-0"
        >
          <small class="text-muted d-block mb-25">Month</small>
          <v-select
            v-model="selectedMonthFilter"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="monthFilterOptions"
            :clearable="true"
            placeholder="All Months"
          />
        </b-col>
        <b-col
          cols="12"
          sm="5"
          md="4"
          class="mb-1 mb-sm-0"
        >
          <small class="text-muted d-block mb-25">Salesperson</small>
          <v-select
            v-model="selectedSalespersonFilter"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="salespersonFilterOptions"
            :clearable="true"
            placeholder="All Salespeople"
            @input="fetchDealPerYearChart"
          />
        </b-col>
      </b-row>
      <app-echart-bar
        v-if="filteredSalesChartData.xAxisData.length > 0"
        :option-data="filteredSalesChartData"
      />
      <div
        v-else
        class="text-center py-3 text-muted font-small-3"
      >
        No sales data available for the selected filters.
      </div>
    </b-card>

    <!-- ===================== MAIN ROW (hidden) ===================== -->
    <!--
    <b-row>
      LEFT: LP Table, RIGHT: Sales Team (replaced by LP cards below)
    </b-row>
    -->

    <!-- ===================== LANDING PAGES CARDS ===================== -->
    <div class="mb-2">
      <div class="d-flex align-items-center justify-content-between mb-1">
        <h4 class="mb-0">
          Landing Pages
        </h4>
        <div class="d-flex gap-50">
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-50"
            :to="{ name: 'landing-pages-list' }"
          >
            <feather-icon
              icon="ListIcon"
              size="14"
              class="mr-25"
            />
            All LPs
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            :to="{ name: 'landing-pages-create' }"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
              class="mr-25"
            />
            Add New LP
          </b-button>
        </div>
      </div>

      <b-row>
        <b-col
          v-for="lp in allLandingPages"
          :key="lp.uuid"
          cols="12"
          sm="6"
          xl="4"
          class="mb-2"
        >
          <b-card class="lp-stat-card h-100 mb-0">

            <!-- Title + conversion rate badge -->
            <div class="d-flex align-items-start justify-content-between mb-1">
              <div
                class="flex-grow-1 mr-1"
                style="min-width: 0;"
              >
                <p class="font-weight-bolder mb-0 lp-card-title">
                  {{ lp.title || lp.label }}
                </p>
                <small
                  v-if="lp.title && lp.label !== lp.title"
                  class="text-muted"
                >{{ lp.label }}</small>
              </div>
              <b-button
                variant="flat-secondary"
                size="sm"
                class="btn-icon flex-shrink-0 p-25"
                :to="{ name: 'landing-pages-edit', params: { id: lp.id } }"
              >
                <feather-icon
                  icon="EditIcon"
                  size="15"
                />
              </b-button>
            </div>

            <!-- Stats row 1: Submissions · Visitors · Conv. Rate -->
            <b-row class="text-center mb-1">
              <b-col cols="4">
                <div class="lp-stat-icon bg-light-primary rounded mb-50">
                  <feather-icon
                    icon="FileTextIcon"
                    size="15"
                    class="text-primary"
                  />
                </div>
                <div class="font-weight-bolder font-medium-2">
                  {{ lp.form_inputs_count }}
                </div>
                <small class="text-muted">Submissions</small>
              </b-col>
              <b-col cols="4">
                <div class="lp-stat-icon bg-light-info rounded mb-50">
                  <feather-icon
                    icon="EyeIcon"
                    size="15"
                    class="text-info"
                  />
                </div>
                <div class="font-weight-bolder font-medium-2">
                  {{ lp.visitor || 0 }}
                </div>
                <small class="text-muted">Visitors</small>
              </b-col>
              <b-col cols="4">
                <div class="lp-stat-icon bg-light-success rounded mb-50">
                  <feather-icon
                    icon="TrendingUpIcon"
                    size="15"
                    class="text-success"
                  />
                </div>
                <div class="font-weight-bolder font-medium-2">
                  {{ conversionRate(lp) }}%
                </div>
                <small class="text-muted">Conv. Rate</small>
              </b-col>
            </b-row>

            <!-- Stats row 2: Total Deal · Revenue · Close Rate -->
            <b-row class="text-center mb-1">
              <b-col cols="4">
                <div class="lp-stat-icon bg-light-danger rounded mb-50">
                  <feather-icon
                    icon="CheckCircleIcon"
                    size="15"
                    class="text-danger"
                  />
                </div>
                <div class="font-weight-bolder font-medium-2">
                  {{ lpTotalDeals(lp) }}
                </div>
                <small class="text-muted">Total Deal</small>
              </b-col>
              <b-col cols="4">
                <div class="lp-stat-icon bg-light-warning rounded mb-50">
                  <feather-icon
                    icon="DollarSignIcon"
                    size="15"
                    class="text-warning"
                  />
                </div>
                <div class="font-weight-bolder font-medium-2 text-truncate px-25">
                  {{ lpDealPrice(lp) > 0 ? formatCurrency(lpDealPrice(lp)) : '—' }}
                </div>
                <small class="text-muted">Revenue</small>
              </b-col>
              <b-col cols="4">
                <div class="lp-stat-icon bg-light-secondary rounded mb-50">
                  <feather-icon
                    icon="TargetIcon"
                    size="15"
                    class="text-secondary"
                  />
                </div>
                <div class="font-weight-bolder font-medium-2">
                  {{ closeRate(lp) }}%
                </div>
                <small class="text-muted">Close Rate</small>
              </b-col>
            </b-row>

            <!-- View Leads button -->
            <b-button
              variant="outline-primary"
              size="sm"
              block
              :to="{ name: 'leads-landing-page', params: { uuid: lp.uuid } }"
            >
              <feather-icon
                icon="UsersIcon"
                size="12"
                class="mr-25"
              />
              View Leads
            </b-button>

          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- ===================== FLOATING ACTION BUTTONS ===================== -->
    <div class="fab-container">
      <div
        v-b-tooltip.hover.left="'AI Insights'"
        class="fab-item"
      >
        <b-button
          variant="primary"
          class="fab-btn rounded-circle"
          @click="openChatbot"
        >
          <feather-icon
            icon="MessageCircleIcon"
            size="20"
          />
        </b-button>
      </div>
      <div
        v-b-tooltip.hover.left="'Add Manual Submission'"
        class="fab-item"
      >
        <b-button
          variant="success"
          class="fab-btn rounded-circle"
          @click="$refs.manualSubmissionModal.show()"
        >
          <feather-icon
            icon="FilePlusIcon"
            size="20"
          />
        </b-button>
      </div>
    </div>

    <!-- ===================== AI INSIGHTS MODAL ===================== -->
    <b-modal
      ref="chatbotModal"
      title="AI Insights"
      ok-only
      ok-title="Close"
      ok-variant="secondary"
      size="lg"
      scrollable
    >
      <div
        v-if="chatbotLoading"
        class="text-center py-4"
      >
        <b-spinner variant="primary" />
        <p class="mt-1 text-muted">
          Loading insights...
        </p>
      </div>
      <template v-else-if="chatbotData">
        <!-- Summary Metrics -->
        <b-row class="mb-1">
          <b-col
            cols="6"
            md="3"
            class="text-center mb-1"
          >
            <div class="font-weight-bold h4 text-primary mb-0">
              {{ chatbotData.pipeline.total_leads }}
            </div>
            <small class="text-muted">Total Leads</small>
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="text-center mb-1"
          >
            <div class="font-weight-bold h4 text-success mb-0">
              {{ chatbotData.pipeline.conversion_rate }}%
            </div>
            <small class="text-muted">Deal Rate</small>
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="text-center mb-1"
          >
            <div class="font-weight-bold h4 text-info mb-0">
              {{ chatbotData.landing_pages.total_landing_pages }}
            </div>
            <small class="text-muted">Landing Pages</small>
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="text-center mb-1"
          >
            <div class="font-weight-bold h4 text-warning mb-0">
              {{ chatbotData.sales_team.total_salespersons }}
            </div>
            <small class="text-muted">Salespersons</small>
          </b-col>
        </b-row>

        <!-- Monthly Deals Trend -->
        <h6 class="font-weight-bold border-top pt-1">
          Monthly Deals — {{ chatbotData.trends.year }}
        </h6>
        <div class="monthly-trend-grid mb-1">
          <div
            v-for="monthData in chatbotData.trends.months"
            :key="monthData.month"
            class="trend-cell text-center"
          >
            <div class="font-small-2 text-muted">
              {{ monthData.month }}
            </div>
            <div class="font-weight-bold text-primary">
              {{ monthData.total_deals }}
            </div>
          </div>
        </div>

        <!-- Pipeline Stages -->
        <h6 class="font-weight-bold border-top pt-1">
          Pipeline Stages
        </h6>
        <b-table
          :items="chatbotData.pipeline.stages"
          :fields="[{ key: 'label', label: 'Stage' }, { key: 'total_leads', label: 'Leads', tdClass: 'text-right', thClass: 'text-right' }]"
          small
          class="mb-1"
          striped
        />

        <!-- Top Landing Pages -->
        <h6 class="font-weight-bold border-top pt-1">
          Top Landing Pages by Leads
        </h6>
        <b-list-group
          flush
          class="mb-1"
        >
          <b-list-group-item
            v-for="lp in chatbotData.landing_pages.top_by_leads.slice(0, 5)"
            :key="lp.title"
            class="d-flex justify-content-between align-items-center py-75 px-0"
          >
            <span class="font-small-3">{{ lp.title }}</span>
            <div>
              <b-badge
                variant="light-primary"
                class="mr-50"
              >
                {{ lp.total_leads }} leads
              </b-badge>
              <b-badge variant="light-success">
                {{ lp.conversion_rate }}% deal rate
              </b-badge>
            </div>
          </b-list-group-item>
        </b-list-group>

        <p class="font-small-2 text-muted text-right mb-0">
          Data as of {{ new Date(chatbotData.generated_at).toLocaleString() }}
        </p>
      </template>
      <div
        v-else
        class="text-center py-4 text-muted"
      >
        Unable to load insights. Please try again.
      </div>
    </b-modal>

    <!-- ===================== MANUAL SUBMISSION MODAL ===================== -->
    <b-modal
      ref="manualSubmissionModal"
      title="Add Manual Submission"
      ok-title="Open Form"
      cancel-title="Cancel"
      @ok="goToManualSubmission"
    >
      <p class="text-muted mb-1">
        Select a landing page to manually add a lead submission.
      </p>
      <v-select
        v-model="selectedManualLP"
        :options="allLandingPages"
        :reduce="lp => lp.uncount_slug"
        :get-option-label="lp => lp.title || lp.label"
        placeholder="Search landing page..."
      />
    </b-modal>

  </div>

  <!-- Loading State -->
  <b-container v-else-if="superuserDashboardDataLPExist === false">
    <div class="d-flex justify-content-center">
      <b-spinner
        label="Loading..."
        variant="'primary'"
      />
    </div>
  </b-container>

  <!-- Empty / Onboarding State -->
  <b-container v-else>
    <b-row class="d-flex justify-content-center">
      <b-col
        cols="12"
        md="5"
        class="d-none d-lg-flex align-items-center p-lg-5"
      >
        <div class="w-100 d-lg-flex align-items-center justify-content-center px-5">
          <b-img
            fluid
            :src="require('@/assets/images/logo/su-dashboard.png')"
            alt="Dashboard Image"
          />
        </div>
      </b-col>
      <b-col
        cols="12"
        md="7"
      >
        <div class="text-header">
          <h2 class="font-weight-bolder">
            Keep up your sales team performace fly high and get real-time measurments in no time with just simple steps.
          </h2>
          <h3>
            Questions? Contact us to <a href="mailto:hello@tracebit.net">hello@tracebit.net</a>
          </h3>
        </div>
        <div class="text-body mt-2 d-flex flex-column align-items-start">
          <div class="text-form d-flex py-1">
            <b-avatar
              variant="secondary"
              text="1"
            />
            <div class="d-flex flex-column pl-1">
              <h3 class="font-weight-bolder">
                Create Form for visitors to fill.
              </h3>
              <b-button
                variant="outline-primary"
                pill
                :to="{ name: 'form-managers-create' }"
                style="width: fit-content;"
              >
                Create New Form
              </b-button>
            </div>
          </div>
          <div class="text-landing-pages d-flex py-1">
            <b-avatar
              variant="secondary"
              text="2"
            />
            <div class="d-flex flex-column pl-1">
              <h3 class="font-weight-bolder">
                Add Salesperson to take care the leads.
              </h3>
              <b-button
                variant="outline-primary"
                pill
                :to="{ name: 'user-setting' }"
                style="width: fit-content;"
              >
                Add Salesperson
              </b-button>
            </div>
          </div>
          <div class="text-salesperson d-flex py-1">
            <b-avatar
              variant="secondary"
              text="3"
            />
            <div class="d-flex flex-column pl-1">
              <h3 class="font-weight-bolder">
                Create Landing Page, attach the Form and choose your Salespersons to take care each landing page.
              </h3>
              <b-button
                variant="outline-primary"
                pill
                :to="{ name: 'landing-pages-create' }"
                style="width: fit-content;"
              >
                Create New Landing Page
              </b-button>
            </div>
          </div>
          <div class="text-lead d-flex py-1">
            <b-avatar
              variant="secondary"
              text="4"
            />
            <div class="d-flex flex-column pl-1">
              <h3 class="font-weight-bolder">
                Manage the incoming Leads.
              </h3>
              <b-button
                variant="outline-primary"
                pill
                :to="{ name: 'leads-list' }"
                style="width: fit-content;"
              >
                Go to Lead
              </b-button>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import {
  BRow, BCol, BCard, BButton, BTable, BMedia, BContainer, BSpinner, BAvatar, BImg,
  BModal, BBadge, BListGroup, BListGroupItem, BProgress, VBTooltip,
} from 'bootstrap-vue'
import store from '@/store'
import vSelect from 'vue-select'
import AppEchartBar from '@core/components/charts/echart/AppEchartBar.vue'
import newSalespersonChartTheme from '@/views/menus/dashboard/components/salespersonChartTheme.json'
import newSalesActivityChartTheme from '@/views/menus/dashboard/components/salesActivityChartTheme.json'
import chartjsSuperuserData from '@/views/menus/dashboard/components/chartjsSuperuserData'
import Ripple from 'vue-ripple-directive'
import Chart from 'chart.js'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BSpinner,
    vSelect,
    BMedia,
    AppEchartBar,
    BTable,
    BContainer,
    BAvatar,
    BImg,
    BModal,
    BBadge,
    BListGroup,
    BListGroupItem,
    BProgress,
  },
  filters: {
    formatNumber(value) {
      const number = value.toFixed(2)
      const result = (number - Math.floor(number)) !== 0
      return result ? number : value
    },
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  props: {
    currentUser: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      canvaslandingPageSubmission: null,
      chartlandingPageSubmission: null,
      newSalesActivityChartTheme,
      newSalespersonChartTheme,
      superuserDashboardDataLPExist: false,
      landingPageSubmissionMonthly: null,
      topSalespersonDataChart: null,
      allLandingPages: [],
      yearOptions: [],
      submissionYear: new Date().getFullYear(),
      submissionYearOption: [
        2020, 2021, 2022, 2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030,
      ],

      // Sales chart state
      rawSalesChartData: { xAxisData: [], series: [] },
      rawLandingPagesData: [],
      selectedChartMetric: 'deal',
      selectedSalesActivityYear: new Date().getFullYear(),
      selectedMonthFilter: null,
      selectedSalespersonFilter: null,
      selectedLandingPageSubmission: null,

      // LP table columns
      lpTableFields: [
        { key: 'title', label: 'Landing Page', sortable: true },
        {
          key: 'form_inputs_count',
          label: 'Submissions',
          sortable: true,
          tdClass: 'text-right',
          thClass: 'text-right',
        },
        {
          key: 'conversion_rate',
          label: 'Conv. Rate',
          sortable: false,
          tdClass: 'text-right',
          thClass: 'text-right',
        },
        {
          key: 'lp_actions',
          label: '',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
      ],

      // Sales team table columns
      fieldsTopSalesperson: [
        { key: 'name', label: 'Salesperson', sortable: true },
        {
          key: 'salesperson_histories_count',
          label: 'Leads',
          sortable: true,
          tdClass: 'text-right',
          thClass: 'text-right',
        },
        {
          key: 'salesperson_new_leads_count',
          label: 'N',
          sortable: true,
          tdClass: 'text-right',
          thClass: 'text-right',
        },
        {
          key: 'salesperson_on_process_leads_count',
          label: 'OP',
          sortable: true,
          tdClass: 'text-right',
          thClass: 'text-right',
        },
        {
          key: 'salesperson_deal_leads_count',
          label: 'D',
          sortable: true,
          tdClass: 'text-right',
          thClass: 'text-right',
        },
        {
          key: 'salesperson_fail_leads_count',
          label: 'F',
          sortable: true,
          tdClass: 'text-right',
          thClass: 'text-right',
        },
        {
          key: 'salesperson_conversion',
          label: 'SC',
          sortable: true,
          tdClass: 'text-right',
          thClass: 'text-right',
        },
        {
          key: 'sp_actions',
          label: '',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
      ],

      // AI Insights modal
      chatbotLoading: false,
      chatbotData: null,

      // Manual submission modal
      selectedManualLP: null,
    }
  },
  computed: {
    chartMetricOptions() {
      return [
        { label: 'Deal', value: 'deal' },
        { label: 'Submission', value: 'submission' },
        { label: 'Revenue', value: 'revenue' },
        { label: 'Close Rate', value: 'close_rate' },
      ]
    },
    filteredSalesChartData() {
      const raw = this.rawSalesChartData
      if (!raw.series || !raw.series.length) return raw

      const metricSeries = raw.series.map(s => {
        let data
        if (this.selectedChartMetric === 'submission') {
          data = s.submissions_data || []
        } else if (this.selectedChartMetric === 'revenue') {
          data = s.revenue_data || []
        } else if (this.selectedChartMetric === 'close_rate') {
          data = (s.data || []).map((deals, i) => {
            const subs = (s.submissions_data || [])[i] || 0
            return subs > 0 ? parseFloat(((deals / subs) * 100).toFixed(2)) : 0
          })
        } else {
          data = s.data || []
        }
        return { name: s.name, data, type: 'line', smooth: true }
      })

      if (!this.selectedMonthFilter) {
        return { xAxisData: raw.xAxisData, series: metricSeries }
      }

      const monthIdx = this.selectedMonthFilter.value - 1
      const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return {
        xAxisData: metricSeries.map(s => s.name),
        series: [{
          name: monthLabels[monthIdx],
          type: 'line',
          smooth: true,
          data: metricSeries.map(s => (s.data && s.data[monthIdx]) || 0),
        }],
      }
    },
    monthFilterOptions() {
      return [
        { label: 'January', value: 1 },
        { label: 'February', value: 2 },
        { label: 'March', value: 3 },
        { label: 'April', value: 4 },
        { label: 'May', value: 5 },
        { label: 'June', value: 6 },
        { label: 'July', value: 7 },
        { label: 'August', value: 8 },
        { label: 'September', value: 9 },
        { label: 'October', value: 10 },
        { label: 'November', value: 11 },
        { label: 'December', value: 12 },
      ]
    },
    salespersonFilterOptions() {
      if (!this.topSalespersonDataChart || !this.topSalespersonDataChart.length) return []
      return this.topSalespersonDataChart.map(sp => ({ label: sp.name, value: sp.id }))
    },
  },
  mounted() {
    this.generateLast5Years()
    this.fetchAllLandingPages()
    this.fetchDealPerYearChart()
    this.fetchTopSalespersonLeadDetail()
  },
  methods: {
    async fetchAllLandingPages() {
      await store.dispatch('menu-lead/getAllLandingPages', {
        user_id: this.currentUser.id,
      })
        .then(response => {
          this.allLandingPages = response.data
          Object.entries(response.data).forEach(value => {
            this.selectedLandingPageSubmission = value[1].uuid
          })
          this.fetchTotalMonthlySubmission(this.submissionYear)
          this.superuserDashboardDataLPExist = response.data.length > 0 ? true : null
        })
    },
    async generateLast5Years() {
      const max = new Date().getFullYear()
      const min = max - 5
      for (let i = max; i >= min; i -= 1) {
        this.yearOptions.push(i)
      }
    },
    async fetchTopSalespersonLeadDetail() {
      await store.dispatch('menu-dashboard/getTopSalespersonLeadDetail', {
        superuser_id: this.currentUser.id,
      })
        .then(response => {
          this.topSalespersonDataChart = response.data.data.salesperson
        })
    },
    async fetchDealPerYearChart() {
      const params = {
        year: this.selectedSalesActivityYear,
        user_id: this.$store.state.currentUser.id,
      }
      if (this.selectedSalespersonFilter) {
        params.salesperson_id = this.selectedSalespersonFilter.value
      }
      await store.dispatch('menu-dashboard/getDealPerYearChart', params)
        .then(response => {
          this.rawLandingPagesData = response.data.data.landing_pages
          this.rawSalesChartData = {
            xAxisData: response.data.data.month,
            series: response.data.data.landing_pages.map(s => ({
              name: s.name,
              data: s.data,
              submissions_data: s.submissions_data,
              revenue_data: s.revenue_data,
              type: 'line',
              smooth: true,
            })),
          }
          if (response.data.data.years && response.data.data.years.length > 0) {
            this.yearOptions = response.data.data.years
          }
        })
    },
    async fetchTotalMonthlySubmission(year) {
      this.submissionYear = year
      await store.dispatch('menu-dashboard/getTotalMonthlySubmission', {
        uuid: this.selectedLandingPageSubmission,
        year: this.submissionYear,
      })
        .then(response => {
          this.landingPageSubmissionMonthly = chartjsSuperuserData
          this.landingPageSubmissionMonthly.latestBarChart.data.datasets[0].data = response.data.data.total
          this.landingPageSubmissionMonthly.latestBarChart.data.labels = response.data.data.month
        })

      if (this.chartlandingPageSubmission != null) {
        this.chartlandingPageSubmission.destroy()
      }
      this.canvaslandingPageSubmission = document.getElementById('landingPageSubmission').getContext('2d')
      this.chartlandingPageSubmission = new Chart(this.canvaslandingPageSubmission, this.landingPageSubmissionMonthly.latestBarChart)
      this.chartlandingPageSubmission.update()
    },
    conversionRate(lp) {
      if (!lp.visitor || lp.visitor === 0) return '0.00'
      return ((lp.form_inputs_count / lp.visitor) * 100).toFixed(2)
    },
    lpDealPrice(lp) {
      const match = this.rawLandingPagesData.find(s => s.label === lp.label || s.name === lp.label)
      return match ? (match.total_deal_price || 0) : 0
    },
    lpTotalDeals(lp) {
      const match = this.rawLandingPagesData.find(s => s.label === lp.label || s.name === lp.label)
      if (!match || !match.data) return 0
      return match.data.reduce((sum, v) => sum + v, 0)
    },
    closeRate(lp) {
      const deals = this.lpTotalDeals(lp)
      if (!lp.form_inputs_count || lp.form_inputs_count === 0) return '0.00'
      return ((deals / lp.form_inputs_count) * 100).toFixed(2)
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(value)
    },
    spConversionRate(sp) {
      if (!sp.salesperson_histories_count || sp.salesperson_histories_count === 0) return '0.00'
      return ((sp.salesperson_deal_leads_count / sp.salesperson_histories_count) * 100).toFixed(2)
    },
    async openChatbot() {
      this.$refs.chatbotModal.show()
      if (this.chatbotData) return
      this.chatbotLoading = true
      try {
        const response = await store.dispatch('menu-dashboard/getAiContext')
        this.chatbotData = response.data
      } catch (e) {
        // data stays null, modal shows error state
      } finally {
        this.chatbotLoading = false
      }
    },
    goToManualSubmission(bvModalEvent) {
      if (!this.selectedManualLP) {
        bvModalEvent.preventDefault()
        return
      }
      const route = this.$router.resolve({
        name: 'landing-pages-display-by-slug',
        params: { slug: this.selectedManualLP },
      })
      window.open(route.href, '_blank')
    },
  },
}
</script>

<style>
.dropdown input {
  cursor: pointer;
}

/* Landing page stat cards */
.lp-stat-card {
  border: none;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  transition: box-shadow 0.2s, transform 0.2s;
}
.lp-stat-card:hover {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}
.lp-card-title {
  font-size: 0.95rem;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lp-stat-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.manage-landing-page {
  text-align: right;
  align-self: flex-end;
}

/* Floating action buttons */
.fab-container {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 999;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-end;
}

.fab-btn {
  width: 52px;
  height: 52px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  border-radius: 50% !important;
}

.fab-btn:hover {
  transform: scale(1.08);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.28);
}

/* AI Insights modal — monthly trend grid */
.monthly-trend-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.5rem;
  padding: 0.5rem 0;
}

.trend-cell {
  background: #f8f8f8;
  border-radius: 6px;
  padding: 6px 4px;
}

/* Responsive */
@media screen and (max-width: 576px) {
  .manage-landing-page {
    width: 100%;
    text-align: left;
    margin-top: 1rem;
  }

  .monthly-trend-grid {
    grid-template-columns: repeat(4, 1fr);
  }

  .fab-container {
    bottom: 1rem;
    right: 1rem;
  }
}
</style>
